'use client';

/**
 * `useClaimPrize` — SDK-owned mutation for the burn-to-claim prize-pool
 * withdrawal (post-resolution winners only). The prize pool is a SECOND mint
 * (claim_rights), independent of the tradeable graduated token: burning it
 * pays the pro-rata USDC share. Mirrors `useClaimBackerFees` exactly, only
 * swapping `buildClaimBackerFees` → `buildClaimPrize` and the instruction
 * label. On settle the host refreshes the authoritative indexed position,
 * including claimed amount, remaining basis, and realized P&L.
 */
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { applyConfirmedPrizeClaim } from '../../../cache/position-entity.js';
import { retryTransientSubmission } from '../../../market-creation/retry-submission.js';
import { hashDiagnosticEntity } from '../../../tx-engine/diagnostics/lifecycle.js';
import { announceConfirmedClaim } from './helpers/claim-success.js';
import { trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { showCancelledToast, showClaimSubmittingToast, showFailureToast } from './helpers/toasts.js';
import { generateTraceId } from './helpers/trace.js';
import { useTradingHostContext } from './host-context.js';
import { CLAIM_RECEIPT_QUERY_KEY } from './use-claim-receipt.js';
export function useClaimPrize() {
  const deps = useTradingHostContext();
  const abortControllerRef = useRef(null);
  const mutation = useMutation({
    mutationFn: async params => {
      if (params.signingDeps === undefined) {
        throw new Error('signingDeps is required for claim transactions');
      }
      const signingDeps = params.signingDeps;
      const traceId = generateTraceId();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const startedAt = Date.now();
      deps.txSubmission.startSubmission(traceId, 'claim_prize');
      showClaimSubmittingToast(deps, traceId, abortController);
      try {
        const {
          BN,
          buildClaimPrize
        } = await import('@morfi/contract-sdk');
        const {
          PublicKey
        } = await import('@solana/web3.js');
        deps.txSubmission.updateStage(traceId, 'signing');
        const connection = deps.getConnection();
        const sdkResult = await buildClaimPrize(connection, {
          claimer: new PublicKey(params.activeWallet.address),
          market: new PublicKey(params.marketAddress),
          burnAmount: new BN(params.burnAmountD6)
        });
        // Transient relay failures (an upstream RPC 429 on sendTransaction,
        // a relay timeout) are retried here with the SDK's single retry
        // authority instead of surfacing on the first click: a claim burns
        // the wallet's claim rights, so a landed-then-retried claim fails
        // closed on the burn. Cancel on the toast aborts the backoff too.
        const result = await retryTransientSubmission({
          submitTransaction: deps.submitTx,
          buildResult: {
            instructions: sdkResult.instructions,
            signers: sdkResult.signerKeys
          },
          options: {
            instructionName: 'claim_prize',
            traceId,
            signingDeps,
            activeWallet: params.activeWallet,
            turnkeyDeps: deps.turnkeyDeps,
            signal: abortController.signal
          },
          retryContext: {
            workflow: 'claim_prize',
            workflowTraceId: traceId
          },
          signal: abortController.signal,
          throwIfAborted,
          sleepOrAbort,
          onRetryScheduled: ({
            attempt,
            delayMs,
            result: failed
          }) => deps.logger.warn('claim_prize.submission_retry_scheduled', {
            traceId,
            attempt,
            delayMs,
            errorCode: failed.error.code,
            marketHash: hashDiagnosticEntity(params.marketAddress)
          })
        });
        const claimCtx = {
          traceId,
          instruction: 'claim_prize',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: 0,
          latencyMs: Date.now() - startedAt
        };
        if (result.ok) {
          deps.txSubmission.setSignature(traceId, result.signature);
          deps.txSubmission.updateStage(traceId, 'confirmed');
          deps.queryClient.setQueryData([CLAIM_RECEIPT_QUERY_KEY, params.marketAddress, params.activeWallet.address, deps.getActiveNetwork()], true);
          const transition = applyConfirmedPrizeClaim(deps.queryClient, {
            marketAddress: params.marketAddress,
            walletAddress: params.activeWallet.address,
            burnAmountD6: params.burnAmountD6,
            network: deps.getActiveNetwork(),
            confirmedAtMs: Date.now(),
            logger: deps.logger
          });
          if (transition.status === 'amount-mismatch') {
            deps.logger.warn('claim_prize.position_transition_skipped', {
              reason: transition.status,
              marketHash: hashDiagnosticEntity(params.marketAddress),
              walletHash: hashDiagnosticEntity(params.activeWallet.address)
            });
          }
          void announceConfirmedClaim(deps, {
            kind: 'claim_prize',
            traceId,
            signature: result.signature,
            marketAddress: params.marketAddress,
            walletAddress: params.activeWallet.address
          });
          trackTxSuccess(deps, claimCtx, result.signature);
        } else if (abortController.signal.aborted) {
          showCancelledToast(deps, traceId);
          deps.txSubmission.updateStage(traceId, 'cancelled');
        } else {
          deps.txSubmission.setError(traceId, {
            code: result.error.code,
            userMessageKey: result.error.userMessageKey
          });
          showFailureToast(deps, traceId, result.error);
          trackTxFailure(deps, claimCtx, result.error);
        }
        return result;
      } catch (err) {
        if (abortController.signal.aborted) {
          showCancelledToast(deps, traceId);
          deps.txSubmission.updateStage(traceId, 'cancelled');
          return {
            ok: false,
            error: {
              code: 'TX_CANCELLED',
              stage: 'preflight',
              userMessageKey: 'tx.errors.cancelled',
              traceId,
              recoveryAction: {
                type: 'not_retryable'
              }
            },
            traceId
          };
        }
        deps.txSubmission.setError(traceId, {
          code: 'TX_UNKNOWN',
          userMessageKey: 'tx.errors.unknown'
        });
        showFailureToast(deps, traceId, {
          code: 'TX_UNKNOWN',
          stage: 'preflight',
          userMessageKey: 'tx.errors.unknown',
          traceId,
          recoveryAction: {
            type: 'prompt_retry'
          }
        });
        trackTxException(deps, {
          traceId,
          instruction: 'claim_prize',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: 0,
          latencyMs: Date.now() - startedAt
        }, err);
        throw err;
      }
    },
    onSettled: (_data, _error, params_0) => {
      deps.onMutationSettled('claim_prize', {
        marketAddress: params_0.marketAddress,
        walletAddress: params_0.activeWallet.address
      });
    }
  });
  return {
    ...mutation,
    abortControllerRef
  };
}
class ClaimAbort extends Error {
  name = 'ClaimAbort';
}
function throwIfAborted(signal) {
  if (signal?.aborted) throw new ClaimAbort('Prize claim aborted');
}
async function sleepOrAbort(delayMs, signal) {
  if (delayMs <= 0) return;
  throwIfAborted(signal);
  await new Promise((resolve, reject) => {
    let timer;
    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(new ClaimAbort('Prize claim aborted'));
    };
    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, delayMs);
    signal?.addEventListener('abort', onAbort, {
      once: true
    });
  });
}